import { AgencyRecommendationResponse, RecommendationStatus } from '@/apis/agency/recommendations/types';
import { MdOutlineAccessTime } from 'react-icons/md';
import { formatDate, timeAgo, titleCase } from 'utils/helperFunctions';
import { useTheme, Box, Stack, Typography, Chip, Theme } from '@mui/material';

export default function RecommendationCard({
  item,
  onOpenCallLog
}: {
  item: AgencyRecommendationResponse;
  onOpenCallLog: (id: number) => void;
}) {
  const theme = useTheme();
  return (
    <Box border={`1px solid ${theme.palette.divider}`} borderRadius={3} p={2.5} bgcolor='#fff'>
      <Stack spacing={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1}>
          <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
            <StatusBadge status={item.status} />
            {typeof item.occurrences === 'number' && (
              <Stack
                direction='row'
                alignItems='center'
                gap={1}
                bgcolor={theme.palette.primary.light}
                px={1.2}
                py={0.5}
                borderRadius='12px'
                width='fit-content'
              >
                <Box width={6} height={6} borderRadius='50%' bgcolor={theme.palette.primary.main} />
                <Typography fontSize={12} fontWeight={500} color='primary'>
                  Asked by {item.occurrences} callers
                </Typography>
              </Stack>
            )}
          </Stack>

          <Stack direction='row' alignItems='center' gap={0.5} color='text.secondary'>
            <MdOutlineAccessTime size={16} />
            <Typography fontSize={12}>
              {item.updated_at || item.created_at ? timeAgo(item.updated_at || item.created_at) : '—'}
            </Typography>
          </Stack>
        </Stack>

        <Box borderBottom='1px solid #ECECEC' pb={1.5}>
          <Typography fontWeight={500} fontSize={16}>
            {item.question}
          </Typography>
          {item.description && (
            <Typography color='text.secondary' fontSize={14} mt={0.5}>
              {item.description}
            </Typography>
          )}
        </Box>

        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1}>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {item?.metadata?.interaction_ids?.map((callLogId: number) => (
              <Box key={callLogId} pt={0.5}>
                <Chip
                  label={`call-${callLogId}`}
                  onClick={() => onOpenCallLog(callLogId)}
                  size='small'
                  sx={{
                    bgcolor: '#FAFAFA',
                    fontSize: 12
                  }}
                />
              </Box>
            ))}
          </Stack>
          <Typography fontSize={12} color='text.secondary'>
            {item.created_at ? formatDate(item.created_at) : ''}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function getStatusPalette(theme: Theme, status: RecommendationStatus | undefined) {
  const map: Record<RecommendationStatus, { color: string; bg: string }> = {
    pending: { color: theme.palette.error.main, bg: '#FFF2F2' },
    accepted: { color: theme.palette.success.main, bg: theme.palette.success.light },
    rejected: { color: '#D97706', bg: '#FFFBEB' }
  };
  return status && map[status] ? map[status] : { color: '#374151', bg: '#F3F4F6' };
}

function StatusBadge({ status }: { status: RecommendationStatus | undefined }) {
  const theme = useTheme();
  const { color, bg } = getStatusPalette(theme, status);
  return (
    <Chip
      label={status ? titleCase(status) : '—'}
      sx={{
        fontWeight: 500,
        fontSize: 12,
        paddingY: 2,
        paddingX: 1,
        borderRadius: '10px',
        color,
        bgcolor: bg,
        px: 1.2,
        py: 0.5
      }}
    />
  );
}
